import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { PublicService, BusinessListing, CustomerInfo, PublicReview, PublicLocation, DepositSettings } from '../types';
import {
  getBusinessListing,
  getBusinessListingByDomain,
  getPublicServices,
  getListingImages,
  extractSubdomain,
  getPublicReviews,
  getPublicLocations,
} from '../utils/firebase';
import { countryCodes } from '../data/countryCodes';
import { useBusinessPageTitle } from '../utils/pageTitle';

interface BookingState {
  selectedService: PublicService | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  customerInfo: CustomerInfo;
}

interface BookingContextType {
  bookingState: BookingState;
  setSelectedService: (service: PublicService | null) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedTime: (time: string | null) => void;
  setCustomerInfo: (info: Partial<CustomerInfo>) => void;
  resetBooking: () => void;
  businessListing: BusinessListing | null;
  setBusinessListing: (listing: BusinessListing | null) => void;
  services: PublicService[];
  setServices: (services: PublicService[]) => void;
  reviews: PublicReview[];
  setReviews: (reviews: PublicReview[]) => void;
  locations: PublicLocation[];
  setLocations: (locations: PublicLocation[]) => void;
  selectedLocation: string | null;
  setSelectedLocation: (location: string | null) => void;
  Notes: string | null;
  setNotes: (notes: string | null) => void;
  // Loading state exposed so pages can show loading/error
  dataLoading: boolean;
  dataError: string | null;
  // Pre-computed approved booker status (checked on CustomerDetailsPage)
  isApprovedBooker: boolean | null; // null = not checked yet, true/false = result
  setIsApprovedBooker: (value: boolean | null) => void;
  // Deposit settings — null when deposits are not enabled for this listing
  depositSettings: DepositSettings | null;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

const initialBookingState: BookingState = {
  selectedService: null,
  selectedDate: null,
  selectedTime: null,
  customerInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: 'US',
    notes: '',
  },
};

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [bookingState, setBookingState] = useState<BookingState>(initialBookingState);
  const [businessListing, setBusinessListing] = useState<BusinessListing | null>(null);
  const [services, setServices] = useState<PublicService[]>([]);
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [locations, setLocations] = useState<PublicLocation[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  const [isApprovedBooker, setIsApprovedBooker] = useState<boolean | null>(null);

  useBusinessPageTitle(businessListing);

  // Guard: only fetch once, even if StrictMode double-mounts
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const loadBusinessData = async () => {
      try {
        setDataLoading(true);
        setDataError(null);

        const hostname = window.location.hostname;
        const subdomain = extractSubdomain(hostname);

        if (!subdomain) {
          setDataError('No business subdomain found. Please use: your-business.yoursite.com');
          setDataLoading(false);
          return;
        }

        const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

        // Serve cached business data immediately for instant render on repeat visits
        let cachedListingId: string | null = null;
        try {
          const rawBiz = localStorage.getItem(`biz_${subdomain}`);
          if (rawBiz) {
            const parsed = JSON.parse(rawBiz);
            if (Date.now() - parsed.cachedAt < CACHE_TTL) {
              setBusinessListing(parsed.data);
              cachedListingId = parsed.data.id;
            }
          }
        } catch { /* ignore */ }

        // Seed services from cache if available
        if (cachedListingId) {
          try {
            const rawSvc = localStorage.getItem(`services_${cachedListingId}`);
            if (rawSvc) {
              const parsed = JSON.parse(rawSvc);
              if (Date.now() - parsed.cachedAt < CACHE_TTL) {
                setServices(parsed.data);
              }
            }
          } catch { /* ignore */ }
        }

        // Fetch listing with a timeout so the UI never spins indefinitely.
        const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
          let timeoutId: ReturnType<typeof setTimeout> | null = null;
          const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error('LISTING_LOAD_TIMEOUT')), timeoutMs);
          });
          try {
            return await Promise.race([promise, timeoutPromise]);
          } finally {
            if (timeoutId) clearTimeout(timeoutId);
          }
        };

        const loadListingOnce = async () => {
          let nextListing = await withTimeout(getBusinessListing(subdomain), 25000);
          if (!nextListing && !hostname.includes('localhost')) {
            nextListing = await withTimeout(getBusinessListingByDomain(hostname), 25000);
          }
          return nextListing;
        };

        // Retry one time to absorb transient Firestore/network latency.
        let listing = await loadListingOnce().catch((err) => {
          if ((err as Error)?.message !== 'LISTING_LOAD_TIMEOUT') throw err;
          return null;
        });

        if (!listing) {
          listing = await loadListingOnce();
        }

        if (!listing) {
          setDataError('Business not found. Please check the URL.');
          setDataLoading(false);
          return;
        }

        if (!listing.published) {
          setDataError('Online booking is not currently available for this business.');
          setDataLoading(false);
          return;
        }

        // Fire services + images in parallel (both depend only on listing.id)
        const servicesPromise = getPublicServices(listing.id);
        const imagesPromise = getListingImages(listing.id);
        const reviewsPromise = getPublicReviews(listing.id);
        const locationsPromise = getPublicLocations(listing.id);

        // Set listing so pages can start rendering structure (no images yet — fast!)
        setBusinessListing(listing);

        // Write fresh listing to localStorage cache
        try {
          localStorage.setItem(`biz_${subdomain}`, JSON.stringify({ data: listing, cachedAt: Date.now() }));
        } catch { /* ignore quota errors */ }

        // Await services (already in-flight) — needed before clearing loading gate
        const servicesData = await servicesPromise;
        setServices(servicesData);

        // Write services to cache
        try {
          localStorage.setItem(`services_${listing.id}`, JSON.stringify({ data: servicesData, cachedAt: Date.now() }));
        } catch { /* ignore quota errors */ }

        reviewsPromise.then((reviewsData) => {
          setReviews((reviewsData as PublicReview[]) ?? []);
        });
        locationsPromise.then((locData) => {
          setLocations((locData as PublicLocation[]) ?? []);
        });

        // Page is now interactive — clear the loading gate
        setDataLoading(false);

        // Images may already be resolved or will resolve shortly
        imagesPromise.then((images) => {
          if (images && images.length > 0) {
            setBusinessListing((prev) =>
              prev ? { ...prev, cover_images: images } : prev
            );
          }
        });
      } catch (err) {
        console.error('Error loading business data:', err);
        if ((err as Error)?.message === 'LISTING_LOAD_TIMEOUT') {
          setDataError('Timed out loading booking data. Please refresh and try again.');
        } else {
          setDataError('Failed to load booking information. Please try again later.');
        }
        setDataLoading(false);
      }
    };

    loadBusinessData();
  }, []);

  // Detect user's country from IP and update country code default
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const res = await fetch('https://api.country.is', { signal: AbortSignal.timeout(3000) });
        if (!res.ok) return;
        const data = await res.json();
        const code = data?.country;
        if (code && countryCodes.some((c) => c.code === code)) {
          setBookingState((prev) => ({
            ...prev,
            customerInfo: { ...prev.customerInfo, countryCode: code },
          }));
        }
      } catch {
        // Silently fall back to default (US)
      }
    };
    detectCountry();
  }, []);

  const setSelectedService = (service: PublicService | null) => {
    setBookingState(prev => ({ ...prev, selectedService: service }));
  };

  const setSelectedDate = (date: Date | null) => {
    setBookingState(prev => ({ ...prev, selectedDate: date }));
  };

  const setSelectedTime = (time: string | null) => {
    setBookingState(prev => ({ ...prev, selectedTime: time }));
  };

  const setCustomerInfo = (info: Partial<CustomerInfo>) => {
    setBookingState(prev => ({
      ...prev,
      customerInfo: { ...prev.customerInfo, ...info },
    }));
  };

  const resetBooking = () => {
    setBookingState(initialBookingState);
    setIsApprovedBooker(null);
  };

  return (
    <BookingContext.Provider
      value={{
        bookingState,
        setSelectedService,
        setSelectedDate,
        setSelectedTime,
        setCustomerInfo,
        resetBooking,
        businessListing,
        setBusinessListing,
        services,
        setServices,
        reviews,
        setReviews,
        locations,
        setLocations,
        selectedLocation: null,
        setSelectedLocation: () => { },
        Notes: null,
        setNotes: () => { },
        dataLoading,
        dataError,
        isApprovedBooker,
        setIsApprovedBooker,
        depositSettings:
          businessListing?.booking_site_enabled && businessListing?.deposits_enabled
            ? {
                enabled: true,
                percent: businessListing.deposit_percent ?? 20,
                policyText: businessListing.deposit_policy_text ?? '',
              }
            : null,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}
